/**
 * Promo hero slideshow — dots, swipe, and optional autoplay.
 */
class PromoHero extends HTMLElement {
  /** @type {HTMLElement[]} */
  #slides = [];

  /** @type {HTMLButtonElement[]} */
  #dots = [];

  /** @type {number} */
  #index = 0;

  /** @type {number | null} */
  #timer = null;

  /** @type {number} */
  #interval = 5000;

  /** @type {MediaQueryList | null} */
  #mediaQuery = null;

  /** @type {number | null} */
  #pointerId = null;

  /** @type {number} */
  #startX = 0;

  /** @type {number} */
  #startY = 0;

  /** @type {boolean} */
  #swiping = false;

  /** @type {boolean} */
  #didSwipe = false;

  /** Minimum horizontal movement (px) to count as a slide change. */
  static #SWIPE_THRESHOLD = 40;

  connectedCallback() {
    this.#slides = Array.from(this.querySelectorAll('[data-promo-hero-slide]'));
    this.#dots = Array.from(this.querySelectorAll('[data-promo-hero-dot]'));

    if (this.#slides.length === 0) return;

    const autoplay = this.getAttribute('data-autoplay') === 'true';
    const speed = Number.parseInt(this.getAttribute('data-autoplay-speed') || '5', 10);
    this.#interval = Math.max(3, speed) * 1000;

    this.#dots.forEach((dot) => {
      dot.addEventListener('click', this.#onDotClick);
    });

    this.addEventListener('mouseenter', this.#pause);
    this.addEventListener('mouseleave', this.#resume);
    this.addEventListener('focusin', this.#pause);
    this.addEventListener('focusout', this.#resume);

    if (this.#slides.length > 1) {
      this.addEventListener('pointerdown', this.#onPointerDown);
      this.addEventListener('pointermove', this.#onPointerMove);
      this.addEventListener('pointerup', this.#onPointerUp);
      this.addEventListener('pointercancel', this.#onPointerUp);
      this.addEventListener('click', this.#onClickCapture, true);
    }

    this.#mediaQuery = window.matchMedia('(max-width: 749px)');
    this.#mediaQuery.addEventListener('change', this.#onViewportChange);

    this.#show(0);

    if (autoplay && this.#slides.length > 1) {
      this.#start();
    }
  }

  disconnectedCallback() {
    this.#stop();
    this.#dots.forEach((dot) => {
      dot.removeEventListener('click', this.#onDotClick);
    });
    this.removeEventListener('mouseenter', this.#pause);
    this.removeEventListener('mouseleave', this.#resume);
    this.removeEventListener('focusin', this.#pause);
    this.removeEventListener('focusout', this.#resume);
    this.removeEventListener('pointerdown', this.#onPointerDown);
    this.removeEventListener('pointermove', this.#onPointerMove);
    this.removeEventListener('pointerup', this.#onPointerUp);
    this.removeEventListener('pointercancel', this.#onPointerUp);
    this.removeEventListener('click', this.#onClickCapture, true);
    this.#mediaQuery?.removeEventListener('change', this.#onViewportChange);
  }

  #onViewportChange = () => {
    const activeSlide = this.#slides[this.#index];
    if (activeSlide) this.#syncVideos(activeSlide, true);
  };

  /**
   * @param {MouseEvent} event
   */
  #onDotClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.currentTarget);
    const index = Number.parseInt(target.getAttribute('data-promo-hero-dot') || '0', 10);
    this.#show(index);
    this.#restart();
  };

  /**
   * @param {PointerEvent} event
   */
  #onPointerDown = (event) => {
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    if (this.#pointerId != null) return;

    const target = /** @type {Element | null} */ (event.target);
    if (target?.closest('[data-promo-hero-dot], .promo-hero__dots')) return;

    this.#pointerId = event.pointerId;
    this.#startX = event.clientX;
    this.#startY = event.clientY;
    this.#swiping = false;
    this.#didSwipe = false;
    this.#pause();
  };

  /**
   * @param {PointerEvent} event
   */
  #onPointerMove = (event) => {
    if (this.#pointerId !== event.pointerId) return;

    const deltaX = event.clientX - this.#startX;
    const deltaY = event.clientY - this.#startY;

    if (!this.#swiping) {
      // Let vertical page scroll win until a clear horizontal intent.
      if (Math.abs(deltaY) > Math.abs(deltaX) && Math.abs(deltaY) > 8) {
        this.#pointerId = null;
        this.#resume();
        return;
      }

      if (Math.abs(deltaX) < 8) return;

      this.#swiping = true;
      try {
        this.setPointerCapture(event.pointerId);
      } catch {
        // Some browsers throw if capture isn't available for the pointer.
      }
    }

    if (this.#swiping) {
      event.preventDefault();
    }
  };

  /**
   * @param {PointerEvent} event
   */
  #onPointerUp = (event) => {
    if (this.#pointerId !== event.pointerId) return;

    const deltaX = event.clientX - this.#startX;
    const swiped = this.#swiping && Math.abs(deltaX) >= PromoHero.#SWIPE_THRESHOLD;

    if (swiped) {
      this.#didSwipe = true;
      if (deltaX < 0) {
        this.#show((this.#index + 1) % this.#slides.length);
      } else {
        this.#show((this.#index - 1 + this.#slides.length) % this.#slides.length);
      }
    }

    this.#pointerId = null;
    this.#swiping = false;

    try {
      if (this.hasPointerCapture?.(event.pointerId)) {
        this.releasePointerCapture(event.pointerId);
      }
    } catch {
      // Ignore release errors.
    }

    this.#restart();
  };

  /**
   * Suppress link/button clicks that follow a completed swipe.
   * @param {MouseEvent} event
   */
  #onClickCapture = (event) => {
    if (!this.#didSwipe) return;
    this.#didSwipe = false;
    event.preventDefault();
    event.stopPropagation();
  };

  #pause = () => {
    this.#stop();
  };

  #resume = () => {
    if (this.getAttribute('data-autoplay') === 'true' && this.#slides.length > 1) {
      this.#start();
    }
  };

  #restart() {
    this.#stop();
    if (this.getAttribute('data-autoplay') === 'true' && this.#slides.length > 1) {
      this.#start();
    }
  }

  #start() {
    this.#stop();
    this.#timer = window.setInterval(() => {
      this.#show((this.#index + 1) % this.#slides.length);
    }, this.#interval);
  }

  #stop() {
    if (this.#timer != null) {
      window.clearInterval(this.#timer);
      this.#timer = null;
    }
  }

  /**
   * @param {number} index
   */
  #show(index) {
    if (index < 0 || index >= this.#slides.length) return;

    this.#index = index;

    this.#slides.forEach((slide, i) => {
      const active = i === index;
      slide.classList.toggle('is-active', active);
      slide.setAttribute('aria-hidden', active ? 'false' : 'true');
      this.#syncVideos(slide, active);
    });

    this.#dots.forEach((dot, i) => {
      const active = i === index;
      dot.classList.toggle('is-active', active);
      dot.setAttribute('aria-current', active ? 'true' : 'false');
    });
  }

  /**
   * @param {HTMLElement} slide
   * @param {boolean} active
   */
  #syncVideos(slide, active) {
    const videos = /** @type {HTMLVideoElement[]} */ (
      Array.from(slide.querySelectorAll('video.promo-hero__video'))
    );

    videos.forEach((video) => {
      const wrapper = video.closest('.promo-hero__media-item');
      const visible = !wrapper || window.getComputedStyle(wrapper).display !== 'none';

      if (active && visible) {
        video.play().catch(() => {});
      } else {
        video.pause();
        if (!active) video.currentTime = 0;
      }
    });
  }
}

if (!customElements.get('promo-hero')) {
  customElements.define('promo-hero', PromoHero);
}
